import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, Sparkles, Loader2, Download, ImagePlus, Wand2, MessageSquareText, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const PRESETS = [
  "Festive Diwali offer poster for a printing shop — flex banner, marigold flowers and diyas, vibrant orange and gold, bold headline space",
  "Instagram post: 50% off vinyl stickers, bold modern typography, dark background with orange accent, print shop branding",
  "Grand opening promo for LED name boards — glowing 3D acrylic letters on a shopfront at night, cinematic",
  "Wedding & event printing offer — elegant gold and cream, invitation cards and banners, premium look",
];

export default function AdminAIStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {id, prompt}
  const [savingGallery, setSavingGallery] = useState(false);
  const [caption, setCaption] = useState("");
  const [captionLoading, setCaptionLoading] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const generate = useCallback(async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/generate-image", { prompt });
      setResult(data);
      toast.success("Image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  }, [prompt]);

  const download = async () => {
    try {
      const res = await api.get(`/content/image/${result.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "creative-prints-ai.png"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const saveToGallery = async () => {
    setSavingGallery(true);
    try {
      await api.post("/gallery/from-image", { image_id: result.id, caption: prompt.slice(0, 60) });
      toast.success("Added to website gallery");
    } catch { toast.error("Could not save to gallery"); }
    finally { setSavingGallery(false); }
  };

  const writeCaption = async () => {
    setCaptionLoading(true);
    try {
      const { data } = await api.post("/ai/caption", { prompt });
      setCaption(data.caption || "");
      toast.success("Caption drafted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Caption failed");
    } finally {
      setCaptionLoading(false);
    }
  };

  if (!user || !user.email) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><Sparkles size={20} /></span>
            <h1 className="font-heading text-base font-extrabold text-slate-900">AI Studio</h1>
          </div>
          <Link to="/admin/dashboard" data-testid="ai-back-btn"><Button variant="outline" size="sm" className="gap-2"><ArrowLeft size={15} /> Dashboard</Button></Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8 grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-lg font-bold text-slate-900">Describe your image</h2>
          <p className="mt-1 text-sm text-slate-500">Generate marketing posts, offers or service visuals with Nano Banana AI.</p>
          <Textarea
            data-testid="ai-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            className="mt-4"
            placeholder="e.g. Diwali flex banner offer poster, marigold and diyas, orange & gold, space for shop name"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {PRESETS.map((p, i) => (
              <button key={i} data-testid={`ai-preset-${i}`} onClick={() => setPrompt(p)} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary text-left transition-colors">
                {p.split(" ").slice(0, 4).join(" ")}…
              </button>
            ))}
          </div>
          <Button data-testid="ai-generate-btn" onClick={generate} disabled={loading} className="mt-5 w-full gap-2 bg-primary hover:bg-amber-700 text-white font-semibold h-11">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Generating…</> : <><Wand2 size={18} /> Generate image</>}
          </Button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-lg font-bold text-slate-900">Result</h2>
          <div className="mt-4 aspect-square w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 grid place-items-center">
            {loading ? (
              <div className="flex flex-col items-center gap-2 text-slate-400"><Loader2 size={28} className="animate-spin" /><span className="text-sm">Creating your image…</span></div>
            ) : result ? (
              <img data-testid="ai-result-image" src={`${API_BASE}/content/image/${result.id}`} alt="AI generated" className="h-full w-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-2 text-slate-300"><ImagePlus size={40} /><span className="text-sm">Your image will appear here</span></div>
            )}
          </div>
          {result && (
            <div className="mt-4 flex gap-2">
              <Button data-testid="ai-download-btn" variant="outline" className="flex-1 gap-2" onClick={download}><Download size={16} /> Download</Button>
              <Button data-testid="ai-save-gallery-btn" className="flex-1 gap-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={saveToGallery} disabled={savingGallery}>
                {savingGallery ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Save to Gallery
              </Button>
            </div>
          )}
          {result && (
            <div className="mt-4">
              <Button data-testid="ai-caption-btn" variant="outline" className="w-full gap-2" onClick={writeCaption} disabled={captionLoading}>
                {captionLoading ? <Loader2 size={16} className="animate-spin" /> : <MessageSquareText size={16} />} Write Instagram caption (EN + ಕನ್ನಡ)
              </Button>
              {caption && (
                <div className="mt-3">
                  <Textarea data-testid="ai-caption-text" value={caption} onChange={(e) => setCaption(e.target.value)} rows={8} className="text-sm" />
                  <Button data-testid="ai-caption-copy" variant="outline" size="sm" className="mt-2 gap-2" onClick={() => { navigator.clipboard.writeText(caption); toast.success("Caption copied"); }}>
                    <Copy size={14} /> Copy
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
